import Vue from "vue";
import { v4 as uuidv4 } from "uuid";
import shared from "@lab/store/item.shared.module";
import { dashboard as api } from "@lab/api/dashboards.api";

import Bugsnag from "@/utils/bugsnag";
import { forEach } from "lodash";

const newChartCell = () => {
	return {
		paneltype: "Chart",
		source: null,
		spacing: null,
		title: null,
		type: null,
		uuid: uuidv4()
	};
};

const state = {
	...shared.state,
	layout: null,
	chart: null,
	updated: false
};

const getters = {
	...shared.getters,
	dashboard: (state) => state.item || null,
	layout: (state) => state.layout,
	chart: (state) => state.chart,
	charts: (state) =>
		state.layout
			.map((l) => l.rows)
			.flat()
			.flat(),
	updated: (state) => state.updated
};

const actions = {
	async fetch({ commit, dispatch }, id: number) {
		commit("LOAD_START");

		const _dashboard = await api.get(id).catch((error) => {
			Bugsnag.notify(
				new Error(`Dashboard fetch: ${error.response.data.error}`)
			);
		});

		if (_dashboard) {
			dispatch(`prepare`, _dashboard);
		}

		commit(`LOAD_FINISH`);
	},
	prepare({ commit }, dashboard) {
		// TODO: create script for dashboard layouts that don't have uuid's
		// dashboard.LayoutJSON.forEach(section => {
		// 	section.uuid = uuidv4();

		// 	section.rows.forEach(row => {
		// 		row.forEach(element => {
		// 			element.uuid = uuidv4();
		// 		});
		// 	});
		// });
		commit(`LOAD_ITEM_SUCCESS`, dashboard);

		commit(`SET_DASHBOARD_LAYOUT`, dashboard.LayoutJSON);
	},
	async create({ commit, dispatch, getters }) {
		commit("LOAD_START");

		const data = {
			...getters.dashboard,
			layout: JSON.stringify(getters.layout)
		};

		const _dashboard = await api.create(data).catch((error) => {
			Bugsnag.notify(
				new Error(`Dashboard update: ${error.response.data.error}`)
			);
		});

		if (_dashboard) {
			dispatch(`prepare`, _dashboard);
		}

		return _dashboard;
	},
	async update({ commit, dispatch, getters }) {
		commit("LOAD_START");

		const data = {
			...getters.dashboard,
			layout: JSON.stringify(getters.layout)
		};

		const _dashboard = await api.update(data).catch((error) => {
			Bugsnag.notify(
				new Error(`Dashboard update: ${error.response.data.error}`)
			);
		});

		if (_dashboard) {
			dispatch(`prepare`, _dashboard);
		}
	},
	async updateJson({ commit, dispatch }, dashboard) {
		commit("LOAD_START");
		const _dashboard = await api.update(dashboard).catch((error) => {
			Bugsnag.notify(
				new Error(`Dashboard JSON update: ${error.response.data.error}`)
			);
		});

		if (_dashboard) {
			dispatch(`prepare`, _dashboard);
		}
	},
	async remove({ dispatch, getters }) {
		await api.remove(getters.dashboard.id).catch((error) => {
			Bugsnag.notify(
				new Error(`Dashboard delete: ${error.response.data.error}`)
			);
		});
		await dispatch("dashboardCharts/fetch", null, { root: true });
	}
};

const mutations = {
	...shared.mutations,
	SET_DASHBOARD_LAYOUT(state, layout) {
		Vue.set(state, "layout", layout);
		state.updated = false;
		state.chart = null;
	},
	addSection(state) {
		state.layout.push({
			rows: [[newChartCell()]],
			title: null,
			uuid: uuidv4()
		});
		state.updated = true;
	},
	removeSection(state, uuid) {
		const index = state.layout.findIndex((item) => item.uuid === uuid);
		state.layout.splice(index, 1);
		state.updated = true;
	},
	updateSectionTitle(state, { uuid, title }) {
		const section = state.layout.find((item) => item.uuid === uuid);
		section.title = title;
		state.updated = true;
	},
	updateOrder(state, list) {
		Vue.set(state, "layout", list);
		state.updated = true;
	},
	addSectionRow(state, uuid: string) {
		const section = state.layout.find((item) => item.uuid === uuid);
		section.rows.push([newChartCell()]);
		state.updated = true;
	},
	removeSectionRow(state, { uuid, index }) {
		const section = state.layout.find((item) => item.uuid === uuid);
		section.rows.splice(index, 1);
		state.updated = true;
	},
	updateSectionRowOrder(state, { list, uuid }) {
		const sectionIndex = state.layout.findIndex(
			(item) => item.uuid === uuid
		);
		Vue.set(state.layout[sectionIndex], "rows", list);
		state.updated = true;
	},
	addRowColumn(state, { uuid, index }) {
		const section = state.layout.find((item) => item.uuid === uuid);
		section.rows[index].push(newChartCell());
		state.updated = true;
	},
	updateRowColumnOrder(state, { list, uuid, index }) {
		const sectionIndex = state.layout.findIndex(
			(item) => item.uuid === uuid
		);
		Vue.set(state.layout[sectionIndex].rows, index, list);
		state.updated = true;
	},
	updateName(state, name) {
		Vue.set(state.item, "name", name);
		state.updated = true;
	},
	update(state, { name, value }) {
		Vue.set(state.item, name, value);
		state.updated = true;
	},
	openChart(state, chart) {
		state.chart = chart;
	},
	closeChart(state) {
		state.chart = null;
	},
	updateChart(state, chart) {
		state.layout.forEach((section, sectionIndex) => {
			section.rows.forEach((row, rowIndex) => {
				const columnIndex = row.findIndex((c) => c.uuid === chart.uuid);
				if (columnIndex >= 0) {
					Vue.set(
						state.layout[sectionIndex].rows[rowIndex],
						columnIndex,
						chart
					);
					state.updated = true;
				}
			});
		});
	},
	removeRowColumn(state, { column, uuid, rowindex }) {
		const sectionIndex = state.layout.findIndex(
			(item) => item.uuid === uuid
		);
		const _row = state.layout[sectionIndex].rows[rowindex].filter(
			(c) => c.uuid !== column.uuid
		);
		Vue.set(state.layout[sectionIndex].rows, rowindex, _row);
		state.updated = true;
	}
};

export default { namespaced: true, state, getters, actions, mutations };
